import {
  answerLook,
  EXERCISE_COPY,
  exerciseKindSchema,
  formatClock,
  generatedSheets,
  librarySheets,
  type MathPracticeSheet,
  mathAnswer,
  mathItemLabel,
  mathItemPrompt,
  nextStep,
  type PendingPhoto,
  type Prompt,
  resumeRun,
  runItems,
  type Sheet,
  type SmAnswer,
  sheetAfter,
  usedSheets,
  workbookSheets,
} from '@echo/core';
import {
  type CompleteExerciseInput,
  clearExerciseProgress,
  type ExerciseProgress,
  getExerciseProgress,
  saveExerciseProgress,
  todayIso,
  useAnalytics,
  useAnswerReveal,
  useChosenPassage,
  useCompleteExercise,
  useExerciseMinutes,
  useExerciseTimer,
  useFinishNotice,
  useMathSet,
  useNextMaterial,
  usePinned,
  useWorkbook,
} from '@echo/core/react';
import { AnswerOption, Button } from '@echo/ui-web';
import { useLingui } from '@lingui/react/macro';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Camera, Image as ImageIcon, Trash2, ZoomIn, ZoomOut } from 'lucide-react';
import { type ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import { AccessGuidance } from '@/components/access-guidance';
import { BrianHint } from '@/components/brian-hint';
import { AssistChip, ContentFrame, ExercisePage, TimerControls } from '@/components/exercise-frame';
import { FinishNoticeDialog } from '@/components/finish-notice-dialog';
import { MathFigure } from '@/components/math-figure';
import { PaperCard } from '@/components/paper-card';
import { PassageBody } from '@/components/passage-body';
import { ReadAloudTimer } from '@/components/read-aloud-timer';
import { WebcamSheet } from '@/components/webcam-sheet';
import { useTakePhoto } from '@/lib/camera';
import { preloadCompleteArt } from '@/lib/complete-art';
import { useExerciseChoice } from '@/lib/exercise-routes';

export const Route = createFileRoute('/_app/exercise/$kind/')({
  params: {
    parse: ({ kind }) => ({ kind: exerciseKindSchema.parse(kind) }),
    stringify: ({ kind }) => ({ kind }),
  },
  component: ExerciseScreen,
});

/** Exercise shell shared by reading / writing / math. */
function ExerciseScreen() {
  const { kind } = Route.useParams();
  const chosen = useExerciseChoice();
  const { t } = useLingui();
  const navigate = useNavigate();
  const analytics = useAnalytics();
  const copy = EXERCISE_COPY[kind];
  const minutes = useExerciseMinutes();
  const timer = useExerciseTimer(minutes * 60);
  const clock = formatClock(timer.seconds);
  const complete = useCompleteExercise();
  const next = useNextMaterial();
  const workbook = useWorkbook();
  // Quick math left after an answer picks up where it was: the same start, its answers, and
  // the time already spent — the countdown itself starts over.
  const [resumed] = useState(() =>
    kind === 'math' ? getExerciseProgress(kind, todayIso()) : undefined,
  );
  const [startedAt] = useState(() => resumed?.startedAt ?? new Date().toISOString());
  const activeSeconds = (resumed?.activeSeconds ?? 0) + minutes * 60 - timer.seconds;
  // Pinned, like the sheet and the passage below: "next" is derived live from the sessions
  // and must not move under the person once the exercise has started.
  const prompt = usePinned(next.prompt);
  const sheet = usePinned(next.sheet);
  // Math: a row chosen in Library › Math (or the one a left-off exercise was on) works through
  // that row's next sheet instead of the workbook's.
  const mathChoice = useMathSet(kind === 'math' ? (chosen.mathSet ?? resumed?.mathSet) : undefined);
  const practice = usePinned(mathChoice.loading ? undefined : mathChoice.sheet);
  // Writing: a category of the person's own instead of the workbook prompt.
  const ownChoice =
    kind === 'writing' && chosen.source === 'own' && chosen.choice !== undefined
      ? copy.choose?.options[Number(chosen.choice)]
      : undefined;
  const ownWriting =
    ownChoice?.body && ownChoice.heading
      ? { label: t(ownChoice.label), heading: t(ownChoice.heading), body: t(ownChoice.body) }
      : undefined;

  useEffect(() => {
    analytics.track({ name: 'exercise_started', kind });
  }, [analytics, kind]);

  useEffect(() => preloadCompleteArt(kind), [kind]);

  /**
   * "Done": save what was done (today's session, the attempt, math answers, the workbook
   * item used) and move on. Saving runs in the background — the person is not made to
   * wait on the network for a routine they just completed.
   */
  const finish = (
    details: Pick<
      CompleteExerciseInput,
      | 'source'
      | 'sourceTitle'
      | 'includesLineageUuid'
      | 'alsoIncludesLineageUuids'
      | 'answers'
      | 'photo'
    >,
  ) => {
    analytics.track({ name: 'exercise_completed', kind, seconds: activeSeconds });
    complete.mutate(
      {
        kind,
        startedAt,
        completedAt: new Date().toISOString(),
        activeSeconds,
        ...details,
      },
      // The page photo's object URL is only needed until Core has the attempt; a failed
      // save keeps it (it is in the outbox for this visit).
      { onSuccess: () => details.photo && URL.revokeObjectURL(details.photo.uri) },
    );
    // Writing ends by reading the page out loud; the others are done.
    if (nextStep(kind, 'exercise') === 'readback') {
      void navigate({ to: '/exercise/$kind/readback', params: { kind } });
      return;
    }
    void navigate({
      to: '/exercise/$kind/complete',
      params: { kind },
    });
  };
  // Reading: a passage chosen in the Library (the workbook's or the read-aloud library's, its
  // text read from Core), else "pick for me" reads the next one; otherwise the person's own
  // material.
  const choice = useChosenPassage(chosen.source === 'workbook' ? chosen.passage : undefined);
  const readingSource =
    chosen.source === 'workbook' && (choice.passage || next.passage)
      ? 'workbook'
      : chosen.source === 'other'
        ? 'other'
        : 'own';
  const passage = usePinned(
    readingSource === 'workbook' && !choice.loading ? (choice.passage ?? next.passage) : undefined,
  );
  // Own book / something else: the timer is the page, so none in the toolbar.
  const ownMaterial = kind === 'reading' && readingSource !== 'workbook';
  const sourceTitle = chosen.title ? chosen.title : undefined;
  const finishReading = () =>
    finish(
      ownMaterial
        ? { source: readingSource, ...(sourceTitle ? { sourceTitle } : {}) }
        : {
            source: readingSource,
            ...(passage
              ? { includesLineageUuid: passage.lineageUuid, sourceTitle: passage.title }
              : {}),
          },
    );
  // Reading doesn't stop at zero, and Done with time left asks first ("Don't leave yet").
  const reading = useFinishNotice(timer.seconds, finishReading);
  const controls = (
    <TimerControls
      clock={clock}
      running={timer.running}
      onPause={timer.pause}
      onResume={timer.resume}
      onReset={timer.reset}
    />
  );

  // Until the workbook has answered, what to show is unknown: rendering the fallback copy or
  // a generated sum first and swapping it a moment later read as a flicker. Math also waits a
  // render for its sheet to be pinned: it takes its questions once, when it first shows.
  const pinning =
    kind === 'math' &&
    ((mathChoice.sheet !== undefined && practice === undefined) ||
      (next.sheet !== undefined && sheet === undefined));
  if (!next.ready || (kind === 'reading' && choice.loading) || mathChoice.loading || pinning) {
    return (
      <ExercisePage>
        <ContentFrame>
          <div className="flex min-h-[320px] w-full items-center justify-center">
            <span
              aria-hidden
              className="size-10 animate-spin rounded-full border-4 border-action-primary border-t-transparent"
            />
          </div>
        </ContentFrame>
      </ExercisePage>
    );
  }

  return (
    <ExercisePage toolbar={ownMaterial ? undefined : controls}>
      {kind === 'math' ? (
        <MathBody
          sheet={sheet}
          workbook={workbook.data?.sheets}
          practice={
            practice && mathChoice.set
              ? {
                  sheet: practice,
                  sheets: mathChoice.set.sheets,
                  title: mathChoice.set.title,
                }
              : undefined
          }
          secondsLeft={timer.seconds}
          minutes={minutes}
          resume={resumed}
          startedAt={startedAt}
          activeSeconds={activeSeconds}
          onDone={finish}
        />
      ) : kind === 'writing' ? (
        <WritingBody
          prompt={prompt}
          own={ownWriting}
          secondsLeft={timer.seconds}
          minutes={minutes}
          onDone={(photo) =>
            finish({
              ...(photo ? { photo } : {}),
              ...(ownWriting
                ? { source: 'own', sourceTitle: ownWriting.label }
                : prompt
                  ? { source: 'workbook', includesLineageUuid: prompt.lineageUuid }
                  : { source: 'own' }),
            })
          }
        />
      ) : ownMaterial ? (
        <ContentFrame footer={<Button onClick={reading.done}>{t(copy.done)}</Button>}>
          <ReadAloudTimer
            title={sourceTitle}
            clock={clock}
            running={timer.running}
            onPause={timer.pause}
            onResume={timer.resume}
            onReset={timer.reset}
          />
        </ContentFrame>
      ) : (
        <ContentFrame footer={<Button onClick={reading.done}>{t(copy.done)}</Button>}>
          {/* Instruction, the coach's reassurance, then the passage. */}
          <h1 className="w-full text-headline-sm text-exercise-prompt">{t(copy.prompt)}</h1>
          <BrianHint>{t(copy.hint)}</BrianHint>
          {passage ? (
            <>
              <h2 className="w-full text-headline-lg">{passage.title}</h2>
              <PassageBody body={passage.body} />
            </>
          ) : null}
        </ContentFrame>
      )}
      {kind === 'reading' ? (
        <FinishNoticeDialog
          kind="reading"
          shown={reading.shown}
          minutes={minutes}
          onMarkComplete={reading.markComplete}
          onKeepGoing={reading.keepGoing}
        />
      ) : null}
    </ExercisePage>
  );
}

/**
 * Quick math: one question per card, four options. Clicking an
 * option answers it and the first click counts. A right answer lights up at once with
 * "🎉 Amazing". A wrong one greys out with a small shake and stays so; a second later the
 * right one lights up with a small pulse and "😅 So close" (`answerLook`). Next question
 * moves on once the right answer shows. The questions are a Math library sheet's when a row
 * was chosen in the Library, else the next workbook sheet (20 sums, in printed order);
 * without either a short generated session keeps the screen usable. It is measured by time,
 * not by the sheet: one that runs out with time left asks first, like "Done with math", and
 * keeping going moves on to the next sheet. Leaving after an answer keeps the progress in the
 * browser: Home shows the exercise as in progress and opening it again on the same sheet
 * continues at the next question.
 */
function MathBody({
  sheet,
  workbook,
  practice,
  secondsLeft,
  minutes,
  resume,
  startedAt,
  activeSeconds,
  onDone,
}: {
  sheet: Sheet | undefined;
  /** The workbook's sheets, to go on through after `sheet`. */
  workbook: readonly Sheet[] | undefined;
  /** A Math library sheet, and the row it belongs to with all its sheets. */
  practice:
    | { sheet: MathPracticeSheet; sheets: readonly MathPracticeSheet[]; title: string }
    | undefined;
  secondsLeft: number;
  /** The countdown's length: the day's target the finishing notices name. */
  minutes: number;
  /** Left after an answer earlier today: its sheets, the next question and the answers so far. */
  resume: ExerciseProgress | undefined;
  startedAt: string;
  /** Seconds spent so far, earlier visits included — saved with the progress. */
  activeSeconds: number;
  onDone: (
    details: Pick<
      CompleteExerciseInput,
      'source' | 'sourceTitle' | 'includesLineageUuid' | 'alsoIncludesLineageUuids' | 'answers'
    >,
  ) => void;
}) {
  const { t, i18n } = useLingui();
  const copy = EXERCISE_COPY.math;
  // A stable per-day seed so a generated session doesn't change while you're in it.
  const [daySeed] = useState(() => Number(new Date().toISOString().slice(0, 10).replace(/-/g, '')));
  const [sheets] = useState(() =>
    practice
      ? librarySheets(practice.sheets)
      : sheet
        ? workbookSheets(workbook ?? [sheet])
        : generatedSheets(daySeed),
  );
  const start = practice?.sheet.lineageUuid ?? sheet?.lineageUuid ?? String(daySeed);
  // Left earlier today on this same sheet, it picks up there; begun on another, it starts over.
  const [resumed] = useState(() => resumeRun(sheets, start, resume));
  // The sheets gone through so far: one more each time the person keeps going past the end.
  const [run, setRun] = useState(resumed.run);
  const items = useMemo(() => runItems(sheets, run), [sheets, run]);
  const [index, setIndex] = useState(resumed.index);
  const [selected, setSelected] = useState<number | string | null>(null);
  const [shownAt, setShownAt] = useState(() => Date.now());
  const [answers, setAnswers] = useState<SmAnswer[]>(resumed.answers);
  const question = items[index] ?? (items[0] as NonNullable<(typeof items)[number]>);

  // The current question counts once an answer is picked, whether "Next question" or "Done"
  // follows. From the first answer Home shows Quick math as in progress, and leaving now
  // resumes here.
  const choose = (option: number | string) => {
    if (selected != null) return;
    const recorded: SmAnswer[] = [
      ...answers,
      mathAnswer(question, index, option, Math.max(0, Date.now() - shownAt)),
    ];
    setSelected(option);
    setAnswers(recorded);
    saveExerciseProgress({
      kind: 'math',
      date: todayIso(),
      startedAt,
      activeSeconds: Math.max(0, activeSeconds),
      index: index + 1,
      answers: recorded,
      ...(practice ? { mathSet: practice.sheet.lineageUuid } : {}),
      sheets: run,
    });
  };
  const finish = (recorded: SmAnswer[]) => {
    clearExerciseProgress('math');
    // Every sheet worked on is recorded as used: the first, and any the person kept going to.
    const [first = start, ...more] = usedSheets(sheets, run, recorded);
    const used = {
      includesLineageUuid: first,
      ...(more.length ? { alsoIncludesLineageUuids: more } : {}),
    };
    onDone(
      practice
        ? {
            source: 'workbook',
            sourceTitle: practice.title,
            ...used,
            answers: recorded,
          }
        : sheet
          ? { source: 'workbook', ...used, answers: recorded }
          : { source: 'other', answers: recorded },
    );
  };
  const next = () => {
    if (!reveal.shown) return;
    // The sheet has run out: as with "Done", time left asks first, and at zero it finishes.
    if (index + 1 >= items.length) {
      finishing.done();
      return;
    }
    advance();
  };
  const advance = () => {
    setIndex(index + 1);
    setSelected(null);
    setShownAt(Date.now());
  };
  // The session ends with "Done with math", or "Next" past a sheet's last question, whenever
  // the person wants (like reading and writing): with time left they are asked first, and at
  // zero "You did it!" offers to finish or keep going. Nothing ends it for them. Keeping going
  // once the sheet's last question is answered goes on to the next sheet.
  const finishing = useFinishNotice(secondsLeft, () => finish(answers));
  const keepGoing = () => {
    finishing.keepGoing();
    if (!reveal.shown || index + 1 < items.length) return;
    setRun([...run, sheetAfter(sheets, run.at(-1) ?? start)]);
    advance();
  };
  const reveal = useAnswerReveal(selected, question.answer);
  const answered = selected != null;
  const isCorrect = selected === question.answer;
  const prompt = mathItemPrompt(question.figure) ?? copy.prompt;

  return (
    <ContentFrame
      footer={
        <>
          <Button onClick={finishing.done}>{t(copy.done)}</Button>
          <Button disabled={!reveal.shown} onClick={next}>
            {t`Next question`}
          </Button>
        </>
      }
    >
      <FinishNoticeDialog
        kind="math"
        shown={finishing.shown}
        minutes={minutes}
        onMarkComplete={finishing.markComplete}
        onKeepGoing={keepGoing}
      />
      <h1 className="w-full text-headline-lg text-exercise-prompt">{t(prompt)}</h1>
      <PaperCard>
        <span className="sr-only">{i18n._(mathItemLabel(question.figure))}</span>
        <div aria-hidden>
          <MathFigure figure={question.figure} />
        </div>
      </PaperCard>
      <div className="flex w-full flex-col gap-content-sm">
        {question.options.map((option) => {
          const look = answerLook(option, reveal.state);
          // The right answer after a wrong pick: it catches the eye and says what it is.
          const corrected = look === 'right' && !isCorrect;
          return (
            <Nudge key={option} effect={look === 'wrong' ? 'shake' : corrected ? 'pulse' : null}>
              <AnswerOption
                selected={look === 'right'}
                muted={look === 'wrong'}
                disabled={answered}
                aria-label={
                  look === 'wrong'
                    ? t`${option}, your answer`
                    : corrected
                      ? t`${option}, the correct answer`
                      : undefined
                }
                onClick={() => choose(option)}
              >
                {String(option)}
              </AnswerOption>
            </Nudge>
          );
        })}
      </div>
      {reveal.shown && copy.feedback ? (
        <p
          className="w-full text-center text-headline-md text-on-surface-medium"
          aria-live="polite"
        >
          {isCorrect ? t(copy.feedback.correct) : t(copy.feedback.wrong)}
        </p>
      ) : null}
    </ContentFrame>
  );
}

/** A one-off scale pulse when `active` turns on: the correct answer catching the eye. */
/**
 * A one-off movement when `effect` turns on: a pulse on the right answer catching the eye, a
 * small shake on a wrong pick. Nothing moves when the person has asked for reduced motion.
 */
function Nudge({
  effect,
  children,
}: {
  effect: 'pulse' | 'shake' | null;
  children: React.ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const element = ref.current;
    if (!effect || !element || typeof element.animate !== 'function') return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    const animation =
      effect === 'pulse'
        ? element.animate(
            [{ transform: 'scale(1)' }, { transform: 'scale(1.05)' }, { transform: 'scale(1)' }],
            { duration: 360, easing: 'ease-out' },
          )
        : element.animate(
            [
              { transform: 'rotate(0deg)' },
              { transform: 'rotate(-2deg)' },
              { transform: 'rotate(2deg)' },
              { transform: 'rotate(-1.2deg)' },
              { transform: 'rotate(0deg)' },
            ],
            { duration: 400, easing: 'ease-in-out' },
          );
    return () => animation.cancel();
  }, [effect]);
  return (
    <div ref={ref} className="w-full">
      {children}
    </div>
  );
}

/**
 * Writing: the prompt on the paper card,
 * then a photo of the page. The photo is optional: "Done writing" completes the exercise with
 * or without one; a photo can be zoomed to read back from, and is what Progress will show
 * later. On a phone the camera is the file picker with `capture`; on a laptop it is the
 * webcam sheet. A chosen file stays in the browser as an object URL until Core has the attempt.
 */
function WritingBody({
  prompt,
  own,
  secondsLeft,
  minutes,
  onDone,
}: {
  prompt: Prompt | undefined;
  /** The person's own kind of writing, when chosen instead of the workbook prompt. */
  own: { label: string; heading: string; body: string } | undefined;
  secondsLeft: number;
  /** The countdown's length: the day's target the finishing notices name. */
  minutes: number;
  /** "Done writing", with the page photo if one was added. */
  onDone: (photo: PendingPhoto | undefined) => void;
}) {
  const { t } = useLingui();
  const copy = EXERCISE_COPY.writing;
  const [photo, setPhoto] = useState<PendingPhoto | null>(null);
  const [zoomed, setZoomed] = useState(false);
  const cameraInput = useRef<HTMLInputElement>(null);
  const libraryInput = useRef<HTMLInputElement>(null);
  // "Take photo" and "Retake photo" alike: the phone's camera app, or the webcam on a laptop.
  const camera = useTakePhoto(cameraInput);
  const pickedFile = (file: File) => {
    setPhoto((current) => {
      if (current) URL.revokeObjectURL(current.uri);
      return { uri: URL.createObjectURL(file), name: file.name || 'page.jpg', mimeType: file.type };
    });
  };
  const picked = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.currentTarget.files?.[0];
    event.currentTarget.value = '';
    if (file) pickedFile(file);
  };
  const remove = () => {
    setPhoto((current) => {
      if (current) URL.revokeObjectURL(current.uri);
      return null;
    });
    setZoomed(false);
  };
  // "Done writing" and the finishing notices' "Mark complete" alike: with the photo, if any.
  const finishing = useFinishNotice(secondsLeft, () => onDone(photo ?? undefined));

  if (camera.guidance) return <AccessGuidance kind="camera" {...camera.guidance} />;

  if (photo && zoomed) {
    return (
      <ContentFrame
        className="overflow-hidden"
        footer={
          <Button variant="secondary" onClick={() => setZoomed(false)}>
            {t`Close`}
          </Button>
        }
      >
        <div className="relative -mx-content-sm -mt-content-lg w-[calc(100%+2*var(--spacing-content-sm))] md:-mx-20 md:-mt-20 md:w-[calc(100%+2*var(--spacing-20))]">
          <img
            src={photo.uri}
            alt={t`Your writing`}
            className="block h-auto w-full object-contain"
          />
          <AssistChip
            className="absolute top-internal-md right-internal-md shadow-elevation-low"
            onClick={() => setZoomed(false)}
          >
            <ZoomOut size={20} aria-hidden />
            {t`Zoom out`}
          </AssistChip>
        </div>
      </ContentFrame>
    );
  }

  return (
    <ContentFrame footer={<Button onClick={finishing.done}>{t(copy.done)}</Button>}>
      <FinishNoticeDialog
        kind="writing"
        shown={finishing.shown}
        minutes={minutes}
        onMarkComplete={finishing.markComplete}
        onKeepGoing={finishing.keepGoing}
      />
      <h1 className="w-full text-headline-md text-exercise-prompt">
        {own ? own.heading : t(copy.prompt)}
      </h1>
      <PaperCard>
        <div className="flex flex-col gap-content-sm">
          {own ? (
            <p className="text-body-lg">{own.body}</p>
          ) : prompt ? (
            <p className="text-body-lg">{prompt.text}</p>
          ) : copy.body ? (
            <p className="text-body-lg">{t(copy.body)}</p>
          ) : null}
          {copy.photoHint ? <BrianHint>{t(copy.photoHint)}</BrianHint> : null}
        </div>
      </PaperCard>
      <input
        ref={cameraInput}
        type="file"
        accept="image/*"
        capture="environment"
        className="sr-only"
        tabIndex={-1}
        aria-hidden
        onChange={picked}
      />
      <input
        ref={libraryInput}
        type="file"
        accept="image/*"
        className="sr-only"
        tabIndex={-1}
        aria-hidden
        onChange={picked}
      />
      <WebcamSheet
        {...camera.webcam}
        title={t`Take photo`}
        facing="environment"
        onCapture={pickedFile}
      />
      {photo == null ? (
        <div className="flex w-full flex-col gap-internal-lg">
          <Button variant="secondary" width="full" onClick={() => void camera.takePhoto()}>
            <Camera size={24} aria-hidden />
            {t`Take photo`}
          </Button>
          <Button variant="secondary" width="full" onClick={() => libraryInput.current?.click()}>
            <ImageIcon size={24} aria-hidden />
            {t`Choose image`}
          </Button>
        </div>
      ) : (
        <>
          <div className="relative h-[210px] w-full overflow-hidden rounded-sm border border-border-weak bg-surface shadow-elevation-low">
            <img src={photo.uri} alt={t`Your writing`} className="size-full object-cover" />
            <AssistChip
              className="absolute top-internal-md right-internal-md"
              onClick={() => setZoomed(true)}
            >
              <ZoomIn size={20} aria-hidden />
              {t`Zoom in`}
            </AssistChip>
          </div>
          <div className="flex w-full flex-col gap-internal-lg">
            <Button variant="secondary" width="full" onClick={() => void camera.takePhoto()}>
              <Camera size={24} aria-hidden />
              {t`Retake photo`}
            </Button>
            <Button variant="destructive-ghost" width="full" onClick={remove}>
              <Trash2 size={24} aria-hidden />
              {t`Remove photo`}
            </Button>
          </div>
        </>
      )}
    </ContentFrame>
  );
}
